import React, { useEffect, useState } from 'react';
import {
  AutorenewIcon,
  Badge,
  Button,
  EditOutlinedIcon,
  FormItem,
  Modal,
  Spinner,
  TextArea,
} from 'elmo-elements';
import { AiSparkleIcon } from '@/components/Icons';
import PickerCard, { PickerCardOption } from '@/components/PickerCard';
import AiCaveatNotice from '@/components/AiCaveatNotice';
import { REGENERATE_MODES } from '@/modules/services/pptImport';
import './RegenerateSlideModal.scss';

const MAX_INSTRUCTION_LENGTH = 500;

const TWEAK_CAVEAT_COPY =
  'Tweaks are AI-generated and may not exactly match your instruction. Review the new slide before you use it.';
const REGENERATE_CAVEAT_COPY =
  'Regenerated slides are AI-generated from your original PowerPoint and may differ from the source. Review the new slide before you use it.';

type AiRegenerateMode =
  | typeof REGENERATE_MODES.TWEAK
  | typeof REGENERATE_MODES.REGENERATE;

const MODES = {
  TWEAK: 'tweak' as const,
  REGENERATE: 'regenerate' as const,
};

type ModeOption = PickerCardOption<
  typeof MODES.TWEAK | typeof MODES.REGENERATE
>;

interface RegenerateSlideModalProps {
  isOpen: boolean;
  isLoading: boolean;
  /** Shown inside the modal when the instruction was blocked or declined (LMS2-5106). */
  errorMessage?: string | null;
  /**
   * Set after an edit timed out: it may still land, so this modal can't send another
   * (LMS2-5018). Reopening the modal starts a new request and clears it.
   */
  submitLocked?: boolean;
  allowRegenerate?: boolean;
  slideIndex?: number;
  onClose: () => void;
  onSubmit: (
    mode: AiRegenerateMode,
    userInstruction: string | undefined,
  ) => void;
}

const RegenerateSlideModal = ({
  isOpen,
  isLoading,
  errorMessage = null,
  submitLocked = false,
  allowRegenerate = true,
  slideIndex = 0,
  onClose,
  onSubmit,
}: RegenerateSlideModalProps): React.ReactElement | null => {
  const [selectedMode, setSelectedMode] = useState<
    typeof MODES.TWEAK | typeof MODES.REGENERATE
  >(MODES.TWEAK);
  const [instruction, setInstruction] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedMode(MODES.TWEAK);
      setInstruction('');
    }
  }, [isOpen]);

  const effectiveMode = allowRegenerate ? selectedMode : MODES.TWEAK;

  if (!isOpen) return null;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const trimmedInstruction = instruction.trim();
  const isTweak = effectiveMode === MODES.TWEAK;
  const canSubmit =
    !isLoading &&
    !submitLocked &&
    (isTweak ? trimmedInstruction.length > 0 : true);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const mode: AiRegenerateMode = isTweak
      ? REGENERATE_MODES.TWEAK
      : REGENERATE_MODES.REGENERATE;
    onSubmit(
      mode,
      trimmedInstruction.length > 0 ? trimmedInstruction : undefined,
    );
  };

  const options: ModeOption[] = [
    {
      key: MODES.TWEAK,
      icon: <EditOutlinedIcon />,
      title: 'Tweak the current version',
      description:
        'Keep the same layout — change copy, colours, or one specific element you describe.',
    },
    ...(allowRegenerate
      ? [
          {
            key: MODES.REGENERATE,
            icon: <AutorenewIcon />,
            title: 'Regenerate from PowerPoint',
            description:
              'Rebuild the slide from your original PowerPoint. Best when the first conversion missed the mark.',
          },
        ]
      : []),
  ];

  return (
    <Modal
      className="regenerate-slide-modal"
      type="medium"
      isOpened={isOpen}
      closeModal={handleClose}
      title="Edit slide with AI"
      hasCloseFooterButton={true}
      primaryButton={
        <div className="regenerate-slide-modal__actions">
          <Button
            type="default"
            isUppercase={false}
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            isUppercase={false}
            onClick={handleSubmit}
            isDisabled={!canSubmit}
            icon={
              isLoading ? <Spinner size="s" /> : <AiSparkleIcon size="sm" />
            }
          >
            Generate new slide
          </Button>
        </div>
      }
      closeLabel="Close"
    >
      {allowRegenerate ? (
        <div className="picker-grid">
          {options.map((option) => (
            <PickerCard
              key={option.key}
              option={option}
              isSelected={effectiveMode === option.key}
              isDisabled={isLoading}
              onClick={setSelectedMode}
            />
          ))}
        </div>
      ) : (
        // No source PowerPoint to regenerate from, so the mode is fixed and shown as
        // a static description rather than a selectable card (LMS2-5106).
        <p className="regenerate-slide-modal__subheading">
          Tweak the current version &mdash; keep the same layout; change copy,
          colours, or one specific element you describe.
        </p>
      )}

      <FormItem
        label={isTweak ? 'What should change?' : 'Any specific direction?'}
        labelAddon={
          <span className="regenerate-slide-modal__label-addon">
            {isTweak && (
              <span className="regenerate-slide-modal__required">*</span>
            )}
            <span className="regenerate-slide-modal__label-hint">
              {isTweak ? 'Required' : 'Optional'}
            </span>
          </span>
        }
        message={`${instruction.length} / ${MAX_INSTRUCTION_LENGTH}`}
      >
        <TextArea
          name="instruction"
          value={instruction}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setInstruction(e.target.value.slice(0, MAX_INSTRUCTION_LENGTH))
          }
          placeholder={
            isTweak
              ? 'e.g. "Change the title colour to red", "Replace the photo with a chart showing the three steps", "Shorten the second paragraph"'
              : 'e.g. "Keep it text-heavy and use bullet points", or "Match the tone of the previous slide". Leave blank to follow the original.'
          }
          rows={4}
          isDisabled={isLoading}
        />
      </FormItem>

      {errorMessage && (
        <div className="regenerate-slide-modal__error" role="alert">
          {errorMessage}
        </div>
      )}

      <AiCaveatNotice
        className="regenerate-slide-modal__caveat"
        copy={isTweak ? TWEAK_CAVEAT_COPY : REGENERATE_CAVEAT_COPY}
      />

      <div className="regenerate-slide-modal__info">
        <p className="regenerate-slide-modal__alert-title">
          Your original slide is duplicated &mdash; the AI edit is applied to
          the copy.
        </p>
        <div className="regenerate-slide-modal__diagram">
          <div className="regenerate-slide-modal__diagram-card regenerate-slide-modal__diagram-card--original">
            <Badge
              type="grey"
              role="status"
              className="regenerate-slide-modal__badge-original"
            >
              Slide {slideIndex}
            </Badge>
            <span className="regenerate-slide-modal__diagram-label">
              Original &middot; kept as-is
            </span>
          </div>
          <span className="regenerate-slide-modal__diagram-arrow">
            <strong>duplicated</strong> &rarr;
          </span>
          <div className="regenerate-slide-modal__diagram-card regenerate-slide-modal__diagram-card--new">
            <Badge
              type="info"
              role="status"
              className="regenerate-slide-modal__badge-new"
            >
              Slide {slideIndex + 1}
            </Badge>
            <span className="regenerate-slide-modal__diagram-label regenerate-slide-modal__diagram-label--new">
              <AiSparkleIcon size="xs" />
              Edited with AI
            </span>
          </div>
        </div>
        <p className="regenerate-slide-modal__info-hint">
          Both slides sit side by side in the module &mdash; review them, then
          delete whichever you don&apos;t want.
        </p>
      </div>
    </Modal>
  );
};

export default RegenerateSlideModal;
